import MobileMenu, { MobileMenuCSS } from "./MobileMenu";
import { supabase } from "./supabaseClient";
import { useState, useEffect, useCallback } from "react";
import { useToast, ToastContainer, MeetingListSkeleton } from "./toast";

const SERVER = "https://legisim-server.onrender.com";

const CSS = `
@import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,700;0,800;0,900;1,700&family=IBM+Plex+Sans:wght@300;400;500&family=IBM+Plex+Mono:wght@400;500&display=swap');
*,*::before,*::after{box-sizing:border-box;margin:0;padding:0;}
html,body{height:100%;}
/* Theme variables defined globally in public/theme.css */
body{background:var(--bg);color:var(--text);font-family:var(--font-body);-webkit-font-smoothing:antialiased;}
.page{display:flex;flex-direction:column;height:100vh;overflow:hidden;background:var(--bg);}

/* MASTHEAD */
.legisim-masthead{flex-shrink:0;background:var(--surface);border-bottom:2px solid rgba(201,168,76,0.22);padding:0 1.25rem;display:flex;align-items:center;justify-content:space-between;height:58px;position:relative;z-index:20;}
.legisim-masthead::before{content:'';position:absolute;top:0;left:0;right:0;height:2px;background:linear-gradient(90deg,transparent 0%,var(--gold) 30%,#e8c96a 50%,var(--gold) 70%,transparent 100%);}
.legisim-masthead::after{content:'';position:absolute;inset:0;background-image:linear-gradient(rgba(201,168,76,0.025) 1px,transparent 1px),linear-gradient(90deg,rgba(201,168,76,0.025) 1px,transparent 1px);background-size:44px 44px;pointer-events:none;}
.masthead-left{display:flex;align-items:center;gap:1rem;z-index:1;}
.masthead-right{display:flex;align-items:center;gap:0.6rem;z-index:1;}
.mast-back{background:transparent;border:2px solid var(--gold-dim);border-radius:8px;padding:0.45rem 1.1rem;color:var(--gold);font-family:var(--font-mono);font-size:0.72rem;font-weight:600;cursor:pointer;letter-spacing:0.07em;transition:all 0.18s;white-space:nowrap;}
.mast-back:hover{border-color:var(--gold);background:var(--gold-glow);}
.mast-btn-sm{background:transparent;border:1px solid var(--border);border-radius:8px;padding:0.4rem 1rem;color:var(--muted);font-family:var(--font-mono);font-size:0.7rem;cursor:pointer;letter-spacing:0.06em;transition:all 0.18s;white-space:nowrap;}
.mast-btn-sm:hover{border-color:var(--gold-dim);color:var(--gold);}
.mast-btn-sm.signout:hover{border-color:rgba(192,57,43,0.5);color:var(--red);}
.masthead-wordmark{font-family:var(--font-display);font-size:1.35rem;font-weight:800;letter-spacing:-0.02em;color:var(--text);}
.masthead-wordmark em{font-style:italic;color:var(--gold);}
.masthead-wordmark sup{font-family:var(--font-mono);font-size:0.42rem;color:var(--muted);vertical-align:super;}
.masthead-divider{width:1px;height:20px;background:var(--border);}
.masthead-tagline{font-family:var(--font-mono);font-size:0.58rem;color:var(--muted);letter-spacing:0.12em;text-transform:uppercase;}
.masthead-badge{display:inline-flex;align-items:center;gap:0.3rem;background:rgba(201,168,76,0.08);border:1px solid rgba(201,168,76,0.2);border-radius:100px;padding:0.18rem 0.6rem;font-family:var(--font-mono);font-size:0.54rem;color:var(--gold);letter-spacing:0.1em;text-transform:uppercase;}
.masthead-badge-dot{width:5px;height:5px;border-radius:50%;background:var(--gold);animation:mbpulse 2.5s infinite;}
@keyframes mbpulse{0%,100%{opacity:0.4;transform:scale(0.85)}50%{opacity:1;transform:scale(1.1)}}
.masthead-nav{display:flex;align-items:center;gap:0.3rem;z-index:1;flex-shrink:1;overflow:hidden;}
.mast-btn{background:transparent;border:1px solid var(--border);border-radius:6px;padding:0.36rem 0.7rem;color:var(--muted);font-family:var(--font-mono);font-size:0.64rem;cursor:pointer;letter-spacing:0.07em;transition:all 0.18s;white-space:nowrap;}
.mast-btn:hover{border-color:var(--gold-dim);color:var(--gold);background:rgba(201,168,76,0.05);}
.mast-btn.gold{border-color:var(--gold-dim);color:var(--gold);}
.mast-btn.gold:hover{background:rgba(201,168,76,0.1);}
.mast-btn.active{border-color:var(--gold);color:var(--gold);background:rgba(201,168,76,0.1);}
.mast-sep{width:1px;height:16px;background:var(--border);margin:0 0.1rem;}

/* BODY */
.cal-body{flex:1;display:flex;overflow:hidden;}

/* SIDEBAR */
.cal-sidebar{width:260px;flex-shrink:0;background:var(--surface);border-right:1px solid var(--border);display:flex;flex-direction:column;overflow:hidden;}
.cal-sidebar-top{padding:1rem;border-bottom:1px solid var(--border);display:flex;flex-direction:column;gap:0.5rem;}
.cal-sidebar-title{font-family:var(--font-mono);font-size:0.6rem;color:var(--muted);letter-spacing:0.12em;text-transform:uppercase;padding:0.75rem 1rem 0.25rem;}
.session-card{margin:0.5rem;background:var(--surface2);border:1px solid var(--border);border-radius:10px;padding:0.75rem 1rem;}
.session-card-label{font-family:var(--font-mono);font-size:0.55rem;color:var(--muted);letter-spacing:0.1em;text-transform:uppercase;margin-bottom:0.3rem;}
.session-card-chamber{display:flex;align-items:center;gap:0.4rem;margin-bottom:0.2rem;}
.session-dot{width:7px;height:7px;border-radius:50%;flex-shrink:0;}
.session-dot.house{background:var(--house);}
.session-dot.senate{background:var(--senate);}
.session-chamber-name{font-family:var(--font-mono);font-size:0.64rem;font-weight:500;}
.session-chamber-name.house{color:var(--house);}
.session-chamber-name.senate{color:var(--senate);}
.session-next{font-size:0.78rem;color:var(--text);padding-left:1.1rem;line-height:1.4;}
.filter-btn{flex:1;background:var(--surface2);border:1px solid var(--border);border-radius:6px;padding:0.3rem 0.4rem;color:var(--muted);font-family:var(--font-mono);font-size:0.6rem;cursor:pointer;transition:all 0.15s;text-align:center;}
.filter-btn:hover{border-color:var(--gold-dim);color:var(--gold);}
.filter-btn.active{border-color:var(--gold);color:var(--gold);background:var(--gold-glow);}
.filter-btn.house.active{border-color:var(--house);color:var(--house);background:var(--house-bg);}
.filter-btn.senate.active{border-color:var(--senate);color:var(--senate);background:var(--senate-bg);}
.filter-row{display:flex;gap:0.4rem;padding:0 0.5rem;}
.meeting-list{flex:1;overflow-y:auto;scrollbar-width:thin;scrollbar-color:var(--border) transparent;}
.meeting-item{padding:0.7rem 1rem;border-bottom:1px solid rgba(255,255,255,0.03);cursor:pointer;transition:background 0.15s;}
.meeting-item:hover{background:var(--surface2);}
.meeting-item.selected{background:var(--charcoal);border-left:2px solid var(--gold);}
.meeting-item-top{display:flex;align-items:center;gap:0.4rem;margin-bottom:0.2rem;}
.chamber-pip{width:6px;height:6px;border-radius:50%;flex-shrink:0;}
.chamber-pip.House{background:var(--house);}
.chamber-pip.Senate{background:var(--senate);}
.meeting-committee{font-size:0.8rem;color:var(--text);font-weight:500;line-height:1.3;}
.meeting-meta{font-family:var(--font-mono);font-size:0.58rem;color:var(--muted);margin-top:0.15rem;}
.meeting-date-badge{font-family:var(--font-mono);font-size:0.58rem;padding:0.1rem 0.4rem;border-radius:4px;flex-shrink:0;}
.meeting-date-badge.House{background:var(--house-bg);color:var(--house);border:1px solid rgba(59,111,212,0.2);}
.meeting-date-badge.Senate{background:var(--senate-bg);color:var(--senate);border:1px solid rgba(155,77,202,0.2);}
.sidebar-count{padding:0.5rem 1rem;font-family:var(--font-mono);font-size:0.6rem;color:var(--muted);border-top:1px solid var(--border);}

/* MAIN PANEL */
.cal-main{flex:1;overflow-y:auto;scrollbar-width:thin;scrollbar-color:var(--border) transparent;}

/* EMPTY STATE */
.cal-empty{display:flex;flex-direction:column;align-items:center;justify-content:center;height:100%;gap:1rem;color:var(--muted);padding:3rem;text-align:center;}
.cal-empty-icon{font-size:3rem;opacity:0.3;}
.cal-empty-title{font-family:var(--font-display);font-size:1.4rem;color:var(--text);opacity:0.5;}
.cal-empty-sub{font-size:0.85rem;max-width:360px;line-height:1.6;}

/* DETAIL PANEL */
.cal-detail{padding:2rem;}
.detail-header{background:var(--surface);border:1px solid var(--border);border-radius:16px;padding:2rem;margin-bottom:1.5rem;position:relative;overflow:hidden;}
.detail-header::before{content:'';position:absolute;top:0;left:0;right:0;height:2px;}
.detail-header.House::before{background:linear-gradient(90deg,transparent,var(--house),transparent);}
.detail-header.Senate::before{background:linear-gradient(90deg,transparent,var(--senate),transparent);}
.detail-chamber-badge{display:inline-flex;align-items:center;gap:0.4rem;padding:0.2rem 0.7rem;border-radius:100px;font-family:var(--font-mono);font-size:0.62rem;font-weight:600;letter-spacing:0.08em;margin-bottom:0.75rem;}
.detail-chamber-badge.House{background:var(--house-bg);border:1px solid rgba(59,111,212,0.3);color:var(--house);}
.detail-chamber-badge.Senate{background:var(--senate-bg);border:1px solid rgba(155,77,202,0.3);color:var(--senate);}
.detail-title{font-family:var(--font-display);font-size:1.75rem;font-weight:800;letter-spacing:-0.02em;color:var(--text);margin-bottom:1rem;line-height:1.2;}
.detail-meta-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(160px,1fr));gap:0.75rem;margin-bottom:1.25rem;}
.detail-meta-item{background:var(--surface2);border:1px solid var(--border);border-radius:8px;padding:0.6rem 0.9rem;}
.detail-meta-label{font-family:var(--font-mono);font-size:0.55rem;color:var(--muted);letter-spacing:0.1em;text-transform:uppercase;margin-bottom:0.2rem;}
.detail-meta-value{font-size:0.85rem;color:var(--text);font-weight:500;}
.detail-actions{display:flex;gap:0.75rem;flex-wrap:wrap;}
.action-btn{display:inline-flex;align-items:center;gap:0.4rem;padding:0.45rem 1rem;border-radius:8px;font-family:var(--font-mono);font-size:0.65rem;font-weight:600;cursor:pointer;transition:all 0.18s;text-decoration:none;letter-spacing:0.06em;}
.action-btn.primary{background:var(--gold);border:none;color:#09090e;}
.action-btn.primary:hover{background:#e8c96a;}
.action-btn.secondary{background:transparent;border:1px solid var(--border);color:var(--muted);}
.action-btn.secondary:hover{border-color:var(--gold-dim);color:var(--gold);background:var(--gold-glow);}

/* BILLS SECTION */
.bills-section{background:var(--surface);border:1px solid var(--border);border-radius:16px;padding:1.75rem;}
.section-title{font-family:var(--font-mono);font-size:0.65rem;color:var(--muted);letter-spacing:0.12em;text-transform:uppercase;margin-bottom:1.25rem;padding-bottom:0.75rem;border-bottom:1px solid var(--border);}
.bill-row{padding:0.85rem 0;border-bottom:1px solid rgba(255,255,255,0.03);}
.bill-row:last-child{border-bottom:none;}
.bill-number{font-family:var(--font-mono);font-size:0.75rem;font-weight:600;color:var(--gold);margin-bottom:0.2rem;}
.bill-sponsor{font-family:var(--font-mono);font-size:0.62rem;color:var(--muted);margin-bottom:0.3rem;}
.bill-desc{font-size:0.82rem;color:var(--text);line-height:1.5;}
.no-bills{padding:1.5rem;text-align:center;font-family:var(--font-mono);font-size:0.72rem;color:var(--muted);}

/* LOADING */
.loading-pulse{display:flex;gap:0.3rem;align-items:center;padding:0.5rem;}
.loading-dot{width:6px;height:6px;border-radius:50%;background:var(--gold);animation:ldot 1.2s infinite;}
.loading-dot:nth-child(2){animation-delay:0.2s;}
.loading-dot:nth-child(3){animation-delay:0.4s;}
@keyframes ldot{0%,80%,100%{opacity:0.2;transform:scale(0.8)}40%{opacity:1;transform:scale(1.1)}}

/* DATE GROUP HEADERS */
.cancelled-banner{background:rgba(192,57,43,0.12);border:1px solid rgba(192,57,43,0.35);border-radius:10px;padding:0.75rem 1.25rem;margin-bottom:1.5rem;display:flex;align-items:center;gap:0.75rem;font-family:var(--font-mono);font-size:0.72rem;color:#e85555;letter-spacing:0.05em;}
.cancelled-icon{font-size:1.2rem;flex-shrink:0;}
.meeting-item.cancelled .meeting-committee{text-decoration:line-through;opacity:0.5;}
.meeting-item.cancelled .meeting-meta{color:#e85555;opacity:0.7;}
.cancelled-pip{width:6px;height:6px;border-radius:50%;background:#e85555;flex-shrink:0;}

/* ── MEETING TRACK / CLIENT ASSIGN ── */
.action-btn.tracked{background:rgba(201,168,76,0.1);border-color:var(--gold-dim);color:var(--gold);}
.action-btn.tracked:hover{background:rgba(201,168,76,0.18);}
.assign-modal-overlay{position:fixed;inset:0;background:rgba(0,0,0,0.65);backdrop-filter:blur(4px);z-index:500;display:flex;align-items:center;justify-content:center;padding:1.5rem;}
.assign-modal{background:var(--surface);border:1px solid var(--border);border-radius:14px;width:100%;max-width:480px;overflow:hidden;display:flex;flex-direction:column;}
.assign-modal-head{padding:1.1rem 1.4rem;border-bottom:1px solid var(--border);display:flex;align-items:center;justify-content:space-between;}
.assign-modal-title{font-size:0.85rem;font-weight:700;color:var(--text);}
.assign-modal-sub{font-family:var(--font-mono);font-size:0.62rem;color:var(--muted);margin-top:2px;}
.assign-modal-body{padding:1.1rem 1.4rem;overflow-y:auto;max-height:340px;display:flex;flex-direction:column;gap:0.5rem;}
.assign-client-row{display:flex;align-items:center;gap:0.75rem;padding:0.6rem 0.75rem;border:1px solid var(--border);border-radius:8px;cursor:pointer;transition:all 0.15s;}
.assign-client-row:hover{border-color:var(--gold-dim);background:var(--gold-glow);}
.assign-client-row.selected{border-color:var(--gold);background:rgba(201,168,76,0.08);}
.assign-client-check{width:16px;height:16px;border-radius:4px;border:1px solid var(--border);flex-shrink:0;display:flex;align-items:center;justify-content:center;font-size:0.65rem;}
.assign-client-row.selected .assign-client-check{background:var(--gold);border-color:var(--gold);color:#09090e;}
.assign-client-name{font-size:0.82rem;color:var(--text);font-weight:500;}
.assign-client-org{font-family:var(--font-mono);font-size:0.6rem;color:var(--muted);}
.assign-modal-foot{padding:0.85rem 1.4rem;border-top:1px solid var(--border);display:flex;gap:0.5rem;justify-content:flex-end;}
.assign-btn-cancel{background:transparent;border:1px solid var(--border);border-radius:8px;padding:0.45rem 1rem;font-family:var(--font-mono);font-size:0.65rem;color:var(--muted);cursor:pointer;transition:all 0.15s;}
.assign-btn-cancel:hover{border-color:var(--gold-dim);color:var(--gold);}
.assign-btn-save{background:var(--gold);border:none;border-radius:8px;padding:0.45rem 1.1rem;font-family:var(--font-mono);font-size:0.65rem;font-weight:700;color:#09090e;cursor:pointer;transition:opacity 0.15s;}
.assign-btn-save:disabled{opacity:0.4;cursor:not-allowed;}
.assign-btn-save:not(:disabled):hover{opacity:0.85;}
.assign-empty{font-family:var(--font-mono);font-size:0.7rem;color:var(--muted);text-align:center;padding:1.5rem 0;}
@media(max-width:768px){
  .masthead-tagline{display:none;}
  .masthead-badge{display:none;}
  .masthead-nav{display:none !important;}
  .legisim-masthead{flex-shrink:0;background:var(--surface);border-bottom:2px solid rgba(201,168,76,0.22);padding:0 1.25rem;display:flex;align-items:center;justify-content:space-between;height:58px;position:relative;z-index:20;}
  .cal-body{display:none;}
  .cal-mobile{display:flex;flex-direction:column;flex:1;overflow:hidden;}
  .cal-mobile-list{flex:1;overflow-y:auto;}
  .cal-mobile-header{padding:1rem 1.25rem 0.75rem;border-bottom:1px solid var(--border);background:var(--surface);flex-shrink:0;}
  .cal-mobile-title{font-family:var(--font-display);font-size:1.2rem;font-weight:800;color:var(--text);margin-bottom:0.15rem;}
  .cal-mobile-meta{font-family:var(--font-mono);font-size:0.6rem;color:var(--muted);}
  .cal-mobile-session{padding:0.75rem 1.25rem;background:var(--surface2);border-bottom:1px solid var(--border);display:flex;gap:1.5rem;flex-shrink:0;}
  .cal-mobile-session-item{display:flex;flex-direction:column;gap:0.15rem;}
  .cal-mobile-session-label{font-family:var(--font-mono);font-size:0.55rem;letter-spacing:0.1em;text-transform:uppercase;}
  .cal-mobile-session-label.house{color:var(--house);}
  .cal-mobile-session-label.senate{color:var(--senate);}
  .cal-mobile-session-val{font-size:0.75rem;color:var(--text);}
  .cal-mobile-filters{padding:0.5rem;border-bottom:1px solid var(--border);flex-shrink:0;}
  .cal-mobile-meeting{padding:0.9rem 1.25rem;border-bottom:1px solid rgba(255,255,255,0.04);cursor:pointer;transition:background 0.15s;display:flex;justify-content:space-between;align-items:center;gap:1rem;}
  .cal-mobile-meeting:hover{background:var(--surface2);}
  .cal-mobile-meeting-left{flex:1;min-width:0;}
  .cal-mobile-meeting-name{font-size:0.9rem;font-weight:500;color:var(--text);margin-bottom:0.2rem;}
  .cal-mobile-meeting-meta{font-family:var(--font-mono);font-size:0.62rem;color:var(--muted);}
  .cal-mobile-meeting-arrow{color:var(--muted);font-size:0.8rem;flex-shrink:0;}
  .cal-mobile-date-header{padding:0.35rem 1.25rem;background:var(--charcoal);font-family:var(--font-mono);font-size:0.6rem;color:var(--gold);letter-spacing:0.1em;text-transform:uppercase;position:sticky;top:0;z-index:5;}
  .cal-mobile-back{display:flex;align-items:center;gap:0.5rem;padding:0.75rem 1.25rem;border-bottom:1px solid var(--border);background:var(--surface);font-family:var(--font-mono);font-size:0.65rem;color:var(--gold);cursor:pointer;flex-shrink:0;}
  .cal-mobile-detail{flex:1;overflow-y:auto;}
  .cal-detail{padding:1rem 1.25rem;}
  .detail-meta-grid{grid-template-columns:1fr 1fr;}
  .detail-title{font-size:1.2rem;}
}
@media(min-width:769px){
  .cal-mobile{display:none;}
}

@media(max-width:1200px){
  .mast-btn{padding:0.3rem 0.45rem;font-size:0.58rem;letter-spacing:0.03em;}
  .masthead-nav{gap:0.2rem;}
}
@media(max-width:900px){
  .mast-btn{padding:0.28rem 0.38rem;font-size:0.55rem;}
  .masthead-nav{gap:0.15rem;}
}
@media(max-width:768px){
  .masthead-nav{display:none !important;}
  .legisim-masthead{flex-shrink:0;background:var(--surface);border-bottom:2px solid rgba(201,168,76,0.22);padding:0 1.25rem;display:flex;align-items:center;justify-content:space-between;height:58px;position:relative;z-index:20;}
}


/* ── EVENT TYPE TOGGLE ── */
.cal-type-toggle{display:flex;gap:0;border-bottom:1px solid var(--border);flex-shrink:0;}
.cal-type-btn{flex:1;background:transparent;border:none;border-bottom:2px solid transparent;padding:0.55rem 0.4rem;font-family:var(--font-mono);font-size:0.6rem;letter-spacing:0.08em;text-transform:uppercase;color:var(--muted);cursor:pointer;transition:all 0.15s;margin-bottom:-1px;}
.cal-type-btn:hover{color:var(--text);}
.cal-type-btn.active{color:var(--gold);border-bottom-color:var(--gold);}
.cal-type-btn.meetings.active{color:var(--house);border-bottom-color:var(--house);}
.cal-type-btn.fundraisers.active{color:#c0392b;border-bottom-color:#c0392b;}

/* ── FUNDRAISER LIST ITEMS ── */
.fundraiser-item{padding:0.7rem 1rem;border-bottom:1px solid rgba(255,255,255,0.03);cursor:pointer;transition:background 0.15s;}
.fundraiser-item:hover{background:var(--surface2);}
.fundraiser-item.selected{background:var(--charcoal);border-left:2px solid #c0392b;}
.fi-top{display:flex;align-items:center;gap:0.4rem;margin-bottom:0.2rem;}
.fi-party-pip{width:6px;height:6px;border-radius:50%;flex-shrink:0;}
.fi-party-pip.R{background:#c0392b;}
.fi-party-pip.D{background:var(--house);}
.fi-name{font-size:0.8rem;color:var(--text);font-weight:500;line-height:1.3;}
.fi-meta{font-family:var(--font-mono);font-size:0.58rem;color:var(--muted);margin-top:0.15rem;}
.fi-source-badge{font-family:var(--font-mono);font-size:0.52rem;padding:0.08rem 0.35rem;border-radius:3px;flex-shrink:0;letter-spacing:0.06em;}
.fi-source-badge.HRCC,.fi-source-badge.SRCC{background:rgba(192,57,43,0.12);color:#c0392b;border:1px solid rgba(192,57,43,0.25);}
.fi-source-badge.HDCC,.fi-source-badge.DRCC{background:rgba(59,111,212,0.12);color:var(--house);border:1px solid rgba(59,111,212,0.25);}
.fi-source-badge.Other{background:var(--surface2);color:var(--muted);border:1px solid var(--border);}

/* ── FUNDRAISER DETAIL ── */
.fundraiser-detail-header{background:var(--surface);border:1px solid var(--border);border-radius:16px;padding:2rem;margin-bottom:1.5rem;position:relative;overflow:hidden;}
.fundraiser-detail-header::before{content:'';position:absolute;top:0;left:0;right:0;height:2px;}
.fundraiser-detail-header.R::before{background:linear-gradient(90deg,transparent,#c0392b,transparent);}
.fundraiser-detail-header.D::before{background:linear-gradient(90deg,transparent,var(--house),transparent);}
.fd-source-row{display:flex;align-items:center;gap:0.6rem;margin-bottom:0.75rem;}
.fd-source-badge{display:inline-flex;align-items:center;gap:0.35rem;padding:0.2rem 0.7rem;border-radius:100px;font-family:var(--font-mono);font-size:0.62rem;font-weight:600;letter-spacing:0.08em;}
.fd-source-badge.R{background:rgba(192,57,43,0.1);border:1px solid rgba(192,57,43,0.3);color:#c0392b;}
.fd-source-badge.D{background:var(--house-bg);border:1px solid rgba(59,111,212,0.3);color:var(--house);}
.fd-party-dot{width:6px;height:6px;border-radius:50%;}
.fd-party-dot.R{background:#c0392b;}
.fd-party-dot.D{background:var(--house);}
.fd-title{font-family:var(--font-display);font-size:1.75rem;font-weight:800;letter-spacing:-0.02em;color:var(--text);margin-bottom:1rem;line-height:1.2;}
.fd-ticket-row{display:flex;align-items:center;gap:0.5rem;margin-bottom:1.25rem;flex-wrap:wrap;}
.fd-ticket-badge{background:rgba(201,168,76,0.08);border:1px solid var(--gold-dim);border-radius:8px;padding:0.4rem 0.9rem;font-family:var(--font-mono);font-size:0.72rem;color:var(--gold);font-weight:600;}
.fd-type-badge{background:var(--surface2);border:1px solid var(--border);border-radius:8px;padding:0.4rem 0.9rem;font-family:var(--font-mono);font-size:0.65rem;color:var(--muted);}
.fd-notes{background:var(--surface2);border:1px solid var(--border);border-radius:10px;padding:1rem 1.25rem;margin-top:1.25rem;font-size:0.82rem;color:var(--muted);line-height:1.6;}
.fd-notes-label{font-family:var(--font-mono);font-size:0.55rem;color:var(--muted);letter-spacing:0.1em;text-transform:uppercase;margin-bottom:0.4rem;}

[data-theme="light"] .fundraiser-item{border-bottom-color:rgba(26,39,68,0.06);}
[data-theme="light"] .fundraiser-item:hover{background:#e8ecf4;}
[data-theme="light"] .fundraiser-item.selected{background:#e0e6f0;border-left-color:#c0392b;}
[data-theme="light"] .fundraiser-detail-header{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .fd-notes{background:#f0f2f8;border-color:rgba(26,39,68,0.1);}
[data-theme="light"] .cal-type-btn{color:#6b7280;}
[data-theme="light"] .cal-type-btn:hover{color:#1a1e2e;}

/* ── LIGHT MODE OVERRIDES ── */
[data-theme="light"] body{background:#eef1f7;color:#1a1e2e;}
[data-theme="light"] .page,[data-theme="light"] .app,[data-theme="light"] .bt-app,[data-theme="light"] .tl-app,[data-theme="light"] .map-app,[data-theme="light"] .cf-app{background:#eef1f7;}
[data-theme="light"] .legisim-masthead{background:#1a2744;border-bottom-color:rgba(255,255,255,0.1);}
[data-theme="light"] .legisim-masthead::before{background:linear-gradient(90deg,transparent,var(--gold),transparent);}
[data-theme="light"] .masthead-wordmark{color:#e6e4dc;}
[data-theme="light"] .masthead-wordmark em{color:#c9a84c;}
[data-theme="light"] .masthead-wordmark sup{color:rgba(230,228,220,0.5);}
[data-theme="light"] .mast-back{border-color:rgba(201,168,76,0.6);color:#c9a84c;background:transparent;}
[data-theme="light"] .mast-back:hover{background:rgba(201,168,76,0.12);border-color:#c9a84c;color:#c9a84c;}
[data-theme="light"] .mast-btn-sm{color:#e6e4dc;border-color:rgba(201,168,76,0.4);}
[data-theme="light"] .mast-btn-sm:hover{color:#ffffff;border-color:#c9a84c;background:rgba(201,168,76,0.1);}
[data-theme="light"] .mast-btn-sm.signout:hover{border-color:rgba(192,57,43,0.7);color:#ff6b6b;background:rgba(192,57,43,0.1);}
[data-theme="light"] .sidebar,[data-theme="light"] .bt-left,[data-theme="light"] .cal-sidebar,[data-theme="light"] .cf-sidebar,[data-theme="light"] .map-left{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .main,[data-theme="light"] .bt-detail,[data-theme="light"] .cf-main{background:#eef1f7;}
[data-theme="light"] .filter-btn,[data-theme="light"] .vh-filter-btn{background:#f8f9fc;border-color:rgba(26,39,68,0.12);color:#6b7280;}
[data-theme="light"] .filter-btn.active,[data-theme="light"] .vh-filter-btn.active{background:rgba(26,39,68,0.1);border-color:rgba(26,39,68,0.3);color:#1a2744;}
[data-theme="light"] .filter-btn.dem-active{background:var(--dem-bg);border-color:var(--dem);color:var(--dem);}
[data-theme="light"] .filter-btn.rep-active{background:var(--rep-bg);border-color:var(--rep);color:var(--rep);}
[data-theme="light"] .search-input,[data-theme="light"] .chat-input,[data-theme="light"] .bt-search-input,[data-theme="light"] .tl-search-input{background:#f8f9fc;border-color:rgba(26,39,68,0.12);color:#1a1e2e;}
[data-theme="light"] .leg-item.selected{background:#e8ecf4;}
[data-theme="light"] .leg-item.selected::before{background:#1a2744;}
[data-theme="light"] .leg-item:hover{background:#e8ecf4;}
[data-theme="light"] .chat-input-area{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .msg-bubble{background:#f0f2f8;border-color:rgba(26,39,68,0.1);}
[data-theme="light"] .msg.user .msg-bubble{background:#e8ecf4;border-color:rgba(26,39,68,0.2);}
[data-theme="light"] .send-btn{background:#1a2744;color:#f8f9fc;}
[data-theme="light"] .main-topbar,[data-theme="light"] .news-topbar,[data-theme="light"] .chamber-bar,[data-theme="light"] .toggle-bar{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .vh-header,[data-theme="light"] .vh-filters,[data-theme="light"] .vh-stats{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .vh-bill-number,[data-theme="light"] .bt-bill-number,[data-theme="light"] .bt-detail-number,[data-theme="light"] .tl-bill-number,[data-theme="light"] .district-num{color:#1a2744;}
[data-theme="light"] .profile-section-label,[data-theme="light"] .section-label,[data-theme="light"] .form-label,[data-theme="light"] .panel-title,[data-theme="light"] .output-label,[data-theme="light"] .floor-title,[data-theme="light"] .detail-title,[data-theme="light"] .search-section-label{color:#1a2744;}
[data-theme="light"] .input-panel{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .output-panel,[data-theme="light"] .output-body{background:#eef1f7;}
[data-theme="light"] .tl-add-btn,[data-theme="light"] .tl-refresh-btn,[data-theme="light"] .tl-view-btn{color:#1a2744;border-color:rgba(26,39,68,0.2);}
[data-theme="light"] .tl-table-head{background:#e8ecf4;}
[data-theme="light"] .tl-tr:hover{background:#e8ecf4;}
[data-theme="light"] .date-group-header{color:#1a2744;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .detail-header,[data-theme="light"] .profile-header,[data-theme="light"] .donors-section,[data-theme="light"] .bt-detail-header{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .acct-card{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .news-tab.active{color:#1a2744;border-color:#1a2744;}
[data-theme="light"] .story-source{color:#6b7280;}
[data-theme="light"] .profile-sidebar{background:#f8f9fc;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .home-title{color:#1a2744;}
[data-theme="light"] .cf-amount,[data-theme="light"] .cf-total{color:#1a2744;}
[data-theme="light"] .chamber-btn.active{background:rgba(26,39,68,0.1);color:#1a2744;border-color:rgba(26,39,68,0.3);}
[data-theme="light"] .swing-chip{background:rgba(26,39,68,0.1);color:#1a2744;}
[data-theme="light"] .score-cell.pass{background:rgba(37,107,66,0.12);}
[data-theme="light"] .score-cell.fail{background:rgba(176,48,32,0.08);}
[data-theme="light"] .map-tooltip-district{color:#1a2744;}
[data-theme="light"] .watchlist-btn-small,[data-theme="light"] .watch-btn{border-color:rgba(160,120,48,0.4);color:#a07830;}
[data-theme="light"] .vote-history-btn{background:rgba(160,120,48,0.08);border-color:rgba(160,120,48,0.35);color:#a07830;}
[data-theme="light"] .masthead-badge{background:rgba(26,39,68,0.08);border-color:rgba(26,39,68,0.2);}
[data-theme="light"] .masthead-badge-dot{background:#1a2744;}
[data-theme="light"] .mast-btn.gold{border-color:rgba(160,120,48,0.4);color:#a07830;}
[data-theme="light"] .mast-btn.active{background:rgba(26,39,68,0.1);border-color:rgba(26,39,68,0.3);color:#1a2744;}
[data-theme="light"] .issue-tag{background:#e8ecf4;border-color:rgba(26,39,68,0.12);color:#1a1e2e;}
[data-theme="light"] .qp-btn{background:#f0f2f8;border-color:rgba(26,39,68,0.12);color:#6b7280;}
[data-theme="light"] .empty-card{background:#f0f2f8;border-color:rgba(26,39,68,0.12);}
[data-theme="light"] .donor-bar-fill{background:linear-gradient(90deg,#1a2744,#2d4a80);}
[data-theme="light"] .cf-leg-item.active,[data-theme="light"] .bt-bill-item.selected{border-left-color:#1a2744;}
[data-theme="light"] .subject-tag{background:#e8ecf4;border-color:rgba(26,39,68,0.12);color:#1a1e2e;}
[data-theme="light"] .plan-badge,[data-theme="light"] .admin{color:#1a2744;}
[data-theme="light"] .login-card{background:#f8f9fc;border-color:rgba(26,39,68,0.12);box-shadow:0 4px 24px rgba(26,39,68,0.1);}
`;

function LoadingDots() {
  return (
    <div className="loading-pulse">
      <div className="loading-dot" />
      <div className="loading-dot" />
      <div className="loading-dot" />
    </div>
  );
}

function formatDisplayDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric", year: "numeric" });
}

function formatShortDate(dateStr) {
  if (!dateStr) return "";
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
}

function groupByDate(meetings) {
  const groups = {};
  for (const m of meetings) {
    const key = m.date || "TBD";
    if (!groups[key]) groups[key] = [];
    groups[key].push(m);
  }
  return groups;
}

async function handleSignOut(supabaseClient, onNavigate) {
  await supabaseClient.auth.signOut();
  onNavigate('home');
}

export default function LegislativeCalendar({ onNavigate }) {
  const [meetings, setMeetings] = useState([]);
  const [session, setSession] = useState(null);
  const [loading, setLoading] = useState(true);
  const { toasts, toast, dismiss } = useToast();
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [chamberFilter, setChamberFilter] = useState("All");
  const [updated, setUpdated] = useState(null);
  const [cancelledIds, setCancelledIds] = useState(new Set());
  const [mobileView, setMobileView] = useState("list"); // "list" or "detail"
  // Two-track view
  const [calMode, setCalMode] = useState("both"); // "meetings" | "fundraisers" | "both"
  const [fundraisers, setFundraisers] = useState([]);
  const [fundraisersLoading, setFundraisersLoading] = useState(true);
  const [selectedFundraiser, setSelectedFundraiser] = useState(null);
  const [trackedFundraiserIds, setTrackedFundraiserIds] = useState(new Set());
  const [trackedMeetingIds,   setTrackedMeetingIds]   = useState(new Set());
  const [userId,              setUserId]              = useState(null);
  const [clients,             setClients]             = useState([]);
  const [showAssign,          setShowAssign]          = useState(false);
  const [assignMeeting,       setAssignMeeting]       = useState(null);
  const [assignSelected,      setAssignSelected]      = useState(new Set());
  const [assignSaving,        setAssignSaving]        = useState(false);

  // Load user session, clients, and tracked meetings
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      const uid = data?.session?.user?.id;
      if (!uid) return;
      setUserId(uid);
      // Load clients
      supabase.from("clients").select("id,name,organization").eq("user_id", uid).order("name")
        .then(({ data: rows }) => { if (rows) setClients(rows); });
      // Load tracked meetings
      supabase.from("tracked_meetings").select("meeting_id").eq("user_id", uid)
        .then(({ data: rows }) => {
          if (rows) setTrackedMeetingIds(new Set(rows.map(r => r.meeting_id)));
        });
    });
  }, []);

  // Fetch meeting list and session info on mount
  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [meetRes, sessRes] = await Promise.all([
          fetch(`${SERVER}/api/calendar/meetings`),
          fetch(`${SERVER}/api/calendar/session`)
        ]);
        const meetData = await meetRes.json();
        const sessData = await sessRes.json();
        setMeetings(meetData.meetings || []);
        setUpdated(meetData.updated);
        setSession(sessData);
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    };
    fetchAll();
  }, []);

  // Fetch fundraiser events
  useEffect(() => {
    setFundraisersLoading(true);
    fetch(`${SERVER}/api/fundraisers`)
      .then(r => r.json())
      .then(d => { setFundraisers(d.events || []); setFundraisersLoading(false); })
      .catch(() => setFundraisersLoading(false));
  }, []);

  // Load tracked fundraiser IDs for current user
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      const userId = data?.session?.user?.id;
      if (!userId) return;
      supabase.from("tracked_fundraisers").select("fundraiser_id").eq("user_id", userId)
        .then(({ data: rows }) => {
          if (rows) setTrackedFundraiserIds(new Set(rows.map(r => r.fundraiser_id)));
        });
    });
  }, []);

  const handleTrackFundraiser = async (f) => {
    const { data: sessionData } = await supabase.auth.getSession();
    const userId = sessionData?.session?.user?.id;
    if (!userId) return;
    const isTracked = trackedFundraiserIds.has(f.id);
    if (isTracked) {
      await supabase.from("tracked_fundraisers").delete().eq("user_id", userId).eq("fundraiser_id", f.id);
      setTrackedFundraiserIds(prev => { const s = new Set(prev); s.delete(f.id); return s; });
      toast("Event removed from calendar", "info");
    } else {
      await supabase.from("tracked_fundraisers").upsert({ user_id: userId, fundraiser_id: f.id });
      setTrackedFundraiserIds(prev => new Set([...prev, f.id]));
      toast("Event saved to calendar ✓");
    }
  };

  // Track / untrack a committee meeting
  const handleTrackMeeting = async (meeting) => {
    if (!userId) { toast("Sign in to track meetings", "warning"); return; }
    const mid = meeting.id;
    const isTracked = trackedMeetingIds.has(mid);
    if (isTracked) {
      await supabase.from("tracked_meetings").delete().eq("user_id", userId).eq("meeting_id", mid);
      setTrackedMeetingIds(prev => { const s = new Set(prev); s.delete(mid); return s; });
      toast("Meeting removed from tracking", "info");
    } else {
      await supabase.from("tracked_meetings").upsert({
        user_id: userId, meeting_id: mid,
        committee: meeting.committee, chamber: meeting.chamber,
        date: meeting.date, time: meeting.time,
        tracked_at: new Date().toISOString(),
      }, { onConflict: "user_id,meeting_id" });
      setTrackedMeetingIds(prev => new Set([...prev, mid]));
      toast("Meeting tracked ✓");
    }
  };

  // Assign a meeting to one or more clients as a logged meeting note
  const handleAssignSave = async () => {
    if (!userId || assignSelected.size === 0 || !assignMeeting) return;
    setAssignSaving(true);
    const m = assignMeeting;
    const isFundraiser = m.isFundraiser;
    const title = isFundraiser
      ? `Fundraiser — ${m.committee}`
      : `Committee Meeting — ${m.committee || m.committeeName}`;
    const location = m.location || "";
    const date = m.date || m.meetDate || "";
    const notes = isFundraiser
      ? [
          m.time             ? `Time: ${m.time}` : null,
          m.ticket_price_min ? `Tickets: $${m.ticket_price_min}${m.ticket_price_max && m.ticket_price_max !== m.ticket_price_min ? " – $" + m.ticket_price_max : ""}` : null,
          m.chair            ? `RSVP Contact: ${m.chair}` : null,
        ].filter(Boolean).join("\n")
      : [
          m.chair ? `Chair: ${m.chair}` : null,
          m.link  ? `Legislature page: ${m.link}` : null,
        ].filter(Boolean).join("\n");

    for (const clientId of assignSelected) {
      await supabase.from("client_meetings").insert({
        client_id: clientId,
        title,
        meeting_date: date || null,
        location,
        notes,
        legislators: m.chair || "",
        created_at: new Date().toISOString(),
      });
    }
    setAssignSaving(false);
    setShowAssign(false);
    setAssignSelected(new Set());
    const count = assignSelected.size;
    toast(`Assigned to ${count} client${count > 1 ? "s" : ""} ✓`);
  };

  // Fetch detail when a meeting is selected
  const selectMeeting = useCallback(async (meeting) => {
    setSelected(meeting.id);
    setDetail(null);
    setDetailLoading(true);
    if (window.innerWidth <= 768) setMobileView("detail");
    try {
      const res = await fetch(`${SERVER}/api/calendar/meeting/${meeting.id}`);
      const data = await res.json();
      if (data.cancelled) setCancelledIds(prev => new Set([...prev, meeting.id]));
      setDetail({ ...meeting, ...data });
    } catch (e) {
      setDetail(meeting);
    }
    setDetailLoading(false);
  }, []);

  // Auto-open a specific meeting/fundraiser when navigating from Dashboard or Client Portal
  useEffect(() => {
    const mid = sessionStorage.getItem("wc_calendar_meeting_id");
    const mtitle = sessionStorage.getItem("wc_calendar_meeting_title");
    if (!mid && !mtitle) return;
    const normalize = s => (s||"").toLowerCase().replace(/[^a-z0-9]/g, "");
    // Match by ID in meetings
    if (mid && meetings.length > 0) {
      const match = meetings.find(m => String(m.id) === mid);
      if (match) { sessionStorage.removeItem("wc_calendar_meeting_id"); selectMeeting(match); return; }
    }
    // Match by title in meetings
    if (mtitle && meetings.length > 0) {
      const target = normalize(mtitle);
      const match = meetings.find(m => {
        const n = normalize(m.committee);
        return n.includes(target) || target.includes(n);
      });
      if (match) { sessionStorage.removeItem("wc_calendar_meeting_title"); selectMeeting(match); return; }
    }
    // Match by title in fundraisers
    if (mtitle && fundraisers.length > 0) {
      const target = normalize(mtitle);
      const match = fundraisers.find(f => {
        const n = normalize(f.event_name);
        return n.includes(target) || target.includes(n);
      });
      if (match) { sessionStorage.removeItem("wc_calendar_meeting_title"); setSelectedFundraiser(match); setSelected(null); setDetail(null); return; }
    }
  }, [meetings, fundraisers, selectMeeting]);

  const filtered = meetings.filter(m => {
    if (chamberFilter === "All") return true;
    return m.chamber === chamberFilter;
  });

  const grouped = groupByDate(filtered);
  const sortedDates = Object.keys(grouped).sort((a, b) => new Date(a) - new Date(b));

  // Fundraiser grouping
  const fundraiserGrouped = groupByDate(fundraisers);
  const fundraiserDates = Object.keys(fundraiserGrouped).sort((a, b) => new Date(a) - new Date(b));

  // Combined "both" mode - merge and sort all dates
  const allDates = [...new Set([
    ...(calMode !== "fundraisers" ? sortedDates : []),
    ...(calMode !== "meetings" ? fundraiserDates : [])
  ])].sort((a, b) => new Date(a) - new Date(b));

  const showMeetings = calMode === "meetings" || calMode === "both";
  const showFundraisers = calMode === "fundraisers" || calMode === "both";

  const downloadIcs = (id) => {
    window.open(`${SERVER}/api/calendar/ics/${id}`, "_blank");
  };

  return (
    <>
      <style>{CSS}</style>
      <MobileMenuCSS />
      <div className="page">
        {/* MASTHEAD */}
        <div className="legisim-masthead">
          <div className="masthead-left">
            <div className="masthead-wordmark">Whip<em>Count</em><sup>TM</sup></div>
            <button className="mast-back" onClick={() => onNavigate("dashboard")}>← Dashboard</button>
          </div>
          <div className="masthead-right">
            <button className="mast-btn-sm" onClick={() => onNavigate("account")}>Account</button>
            <button className="mast-btn-sm signout" onClick={() => handleSignOut(supabase, onNavigate)}>Sign Out</button>
          </div>
        </div>
        <div className="cal-body">
          {/* SIDEBAR */}
          <div className="cal-sidebar">

            {/* Session status */}
            <div className="cal-sidebar-top">
              <div style={{fontFamily:"var(--font-mono)",fontSize:"0.6rem",color:"var(--muted)",letterSpacing:"0.12em",textTransform:"uppercase"}}>Next Session</div>
              {session ? (
                <div style={{display:"flex",flexDirection:"column",gap:"0.5rem"}}>
                  <div>
                    <div style={{display:"flex",alignItems:"center",gap:"0.4rem",marginBottom:"0.15rem"}}>
                      <div className="session-dot house" />
                      <span style={{fontFamily:"var(--font-mono)",fontSize:"0.62rem",color:"var(--house)",fontWeight:500}}>House</span>
                    </div>
                    <div style={{fontSize:"0.78rem",color:"var(--text)",paddingLeft:"1.1rem",lineHeight:1.4}}>{session.house || "Not scheduled"}</div>
                  </div>
                  <div>
                    <div style={{display:"flex",alignItems:"center",gap:"0.4rem",marginBottom:"0.15rem"}}>
                      <div className="session-dot senate" />
                      <span style={{fontFamily:"var(--font-mono)",fontSize:"0.62rem",color:"var(--senate)",fontWeight:500}}>Senate</span>
                    </div>
                    <div style={{fontSize:"0.78rem",color:"var(--text)",paddingLeft:"1.1rem",lineHeight:1.4}}>{session.senate || "Not scheduled"}</div>
                  </div>
                </div>
              ) : (
                <LoadingDots />
              )}
            </div>

            {/* Event type toggle */}
            <div className="cal-type-toggle">
              <button className={`cal-type-btn${calMode === "both" ? " active" : ""}`} onClick={() => setCalMode("both")}>Both</button>
              <button className={`cal-type-btn meetings${calMode === "meetings" ? " active" : ""}`} onClick={() => { setCalMode("meetings"); setSelectedFundraiser(null); }}>Meetings</button>
              <button className={`cal-type-btn fundraisers${calMode === "fundraisers" ? " active" : ""}`} onClick={() => { setCalMode("fundraisers"); setSelected(null); setDetail(null); }}>Fundraisers</button>
            </div>

            {/* Chamber filter — only when showing meetings */}
            {showMeetings && (
              <div style={{padding:"0.5rem",borderBottom:"1px solid var(--border)"}}>
                <div className="filter-row">
                  {["All","House","Senate"].map(c => (
                    <button
                      key={c}
                      className={`filter-btn${c === "House" ? " house" : c === "Senate" ? " senate" : ""}${chamberFilter === c ? " active" : ""}`}
                      onClick={() => setChamberFilter(c)}
                    >{c}</button>
                  ))}
                </div>
              </div>
            )}

            {/* Combined or single-track list */}
            <div className="meeting-list">
              {(loading && showMeetings) || (fundraisersLoading && showFundraisers) ? (
                <div style={{padding:"1rem"}}><LoadingDots /></div>
              ) : allDates.length === 0 ? (
                <div style={{padding:"1.5rem",textAlign:"center",fontFamily:"var(--font-mono)",fontSize:"0.7rem",color:"var(--muted)"}}>No events found</div>
              ) : allDates.map(date => {
                const dayMeetings = showMeetings ? (grouped[date] || []) : [];
                const dayFundraisers = showFundraisers ? (fundraiserGrouped[date] || []) : [];
                if (dayMeetings.length === 0 && dayFundraisers.length === 0) return null;
                return (
                  <div key={date}>
                    <div className="date-group-header">{formatDisplayDate(date)}</div>
                    {dayMeetings.map(m => (
                      <div
                        key={m.id}
                        className={`meeting-item${selected === m.id ? " selected" : ""}${cancelledIds.has(m.id) ? " cancelled" : ""}`}
                        onClick={() => { selectMeeting(m); setSelectedFundraiser(null); }}
                      >
                        <div className="meeting-item-top">
                          {cancelledIds.has(m.id)
                            ? <div className="cancelled-pip" title="Cancelled" />
                            : <div className={`chamber-pip ${m.chamber}`} />
                          }
                          <span className="meeting-committee">{m.committee}</span>
                        </div>
                        <div className="meeting-meta">
                          {cancelledIds.has(m.id) ? <span style={{color:"#e85555"}}>CANCELLED · </span> : ""}
                          {m.time}{m.chamber ? ` · ${m.chamber}` : ""}
                        </div>
                      </div>
                    ))}
                    {dayFundraisers.map(f => (
                      <div
                        key={f.id}
                        className={`fundraiser-item${selectedFundraiser?.id === f.id ? " selected" : ""}`}
                        onClick={() => { setSelectedFundraiser(f); setSelected(null); setDetail(null); }}
                      >
                        <div className="fi-top">
                          <div className={`fi-party-pip ${f.party}`} />
                          <span className="fi-name">{f.event_name}</span>
                          <span className={`fi-source-badge ${f.source}`}>{f.source}</span>
                        </div>
                        <div className="fi-meta">
                          {f.time_start}{f.time_end ? `–${f.time_end}` : ""}
                          {f.venue ? ` · ${f.venue}` : ""}
                          {f.ticket_price_min ? ` · $${f.ticket_price_min}+` : ""}
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>

            <div className="sidebar-count">
              {showMeetings && !loading && `${filtered.length} meeting${filtered.length !== 1 ? "s" : ""}`}
              {showMeetings && showFundraisers && !loading && !fundraisersLoading && " · "}
              {showFundraisers && !fundraisersLoading && `${fundraisers.length} fundraiser${fundraisers.length !== 1 ? "s" : ""}`}
              {updated && <span style={{display:"block",fontSize:"0.55rem",marginTop:"0.15rem"}}>Updated {new Date(updated).toLocaleTimeString()}</span>}
            </div>
          </div>

          {/* MAIN PANEL */}
          <div className="cal-main">
            {selectedFundraiser ? (
              <div className="cal-detail">
                <div className={`fundraiser-detail-header ${selectedFundraiser.party}`}>
                  <div className="fd-source-row">
                    <span className={`fd-source-badge ${selectedFundraiser.party}`}>
                      <span className={`fd-party-dot ${selectedFundraiser.party}`} />
                      {selectedFundraiser.source} · {selectedFundraiser.party === "R" ? "Republican" : "Democrat"}
                    </span>
                    <span className="fd-type-badge">{selectedFundraiser.event_type || "Event"}</span>
                  </div>
                  <div className="fd-title">{selectedFundraiser.event_name}</div>
                  <div className="detail-meta-grid">
                    <div className="detail-meta-item">
                      <div className="detail-meta-label">Date</div>
                      <div className="detail-meta-value">{formatDisplayDate(selectedFundraiser.date)}</div>
                    </div>
                    <div className="detail-meta-item">
                      <div className="detail-meta-label">Time</div>
                      <div className="detail-meta-value">{selectedFundraiser.time_start || "—"}{selectedFundraiser.time_end ? ` – ${selectedFundraiser.time_end}` : ""}</div>
                    </div>
                    <div className="detail-meta-item">
                      <div className="detail-meta-label">Venue</div>
                      <div className="detail-meta-value">{selectedFundraiser.venue || "—"}</div>
                    </div>
                    <div className="detail-meta-item">
                      <div className="detail-meta-label">Address</div>
                      <div className="detail-meta-value">{selectedFundraiser.address || "—"}</div>
                    </div>
                  </div>
                  <div className="fd-ticket-row">
                    {selectedFundraiser.ticket_price_min && (
                      <span className="fd-ticket-badge">
                        {selectedFundraiser.ticket_price_min === selectedFundraiser.ticket_price_max
                          ? `$${selectedFundraiser.ticket_price_min} / ticket`
                          : `$${selectedFundraiser.ticket_price_min} – $${selectedFundraiser.ticket_price_max} / ticket`}
                      </span>
                    )}
                  </div>
                  <div className="detail-actions">
                    <button className="action-btn primary" onClick={() => window.open(`${SERVER}/api/fundraisers/ics/${selectedFundraiser.id}`, "_blank")}>
                      📥 Add to Calendar (.ics)
                    </button>
                    <button
                      className="action-btn secondary"
                      onClick={() => handleTrackFundraiser(selectedFundraiser)}
                      style={trackedFundraiserIds.has(selectedFundraiser.id) ? {borderColor:"var(--gold-dim)",color:"var(--gold)"} : {}}
                    >
                      {trackedFundraiserIds.has(selectedFundraiser.id) ? "📌 Tracked — Click to Remove" : "＋ Track This Event"}
                    </button>
                    <button
                      className="action-btn secondary"
                      onClick={() => {
                        setAssignMeeting({
                          id: selectedFundraiser.id,
                          committee: selectedFundraiser.event_name,
                          date: selectedFundraiser.date,
                          time: selectedFundraiser.time_start,
                          location: selectedFundraiser.venue || selectedFundraiser.address || "",
                          chair: selectedFundraiser.rsvp_contact || "",
                          link: null,
                          isFundraiser: true,
                          ticket_price_min: selectedFundraiser.ticket_price_min,
                          ticket_price_max: selectedFundraiser.ticket_price_max,
                        });
                        setAssignSelected(new Set());
                        setShowAssign(true);
                      }}
                    >
                      👥 Assign to Client
                    </button>
                    {selectedFundraiser.rsvp_email && (
                      <a className="action-btn secondary" href={`mailto:${selectedFundraiser.rsvp_email}`}>
                        ✉ RSVP via Email
                      </a>
                    )}
                    {selectedFundraiser.rsvp_phone && (
                      <a className="action-btn secondary" href={`tel:${selectedFundraiser.rsvp_phone}`}>
                        📞 {selectedFundraiser.rsvp_phone}
                      </a>
                    )}
                    {selectedFundraiser.address && (
                      <a className="action-btn secondary" href={`https://maps.google.com/?q=${encodeURIComponent(selectedFundraiser.address)}`} target="_blank" rel="noreferrer">
                        🗺 Directions
                      </a>
                    )}
                  </div>
                  {(selectedFundraiser.notes || selectedFundraiser.rsvp_contact || selectedFundraiser.rsvp_email || selectedFundraiser.rsvp_phone) && (
                    <div className="fd-notes">
                      {(selectedFundraiser.rsvp_contact || selectedFundraiser.rsvp_email || selectedFundraiser.rsvp_phone) && (
                        <>
                          <div className="fd-notes-label">RSVP Contact</div>
                          <div style={{marginBottom: selectedFundraiser.notes ? "0.75rem" : 0}}>
                            {selectedFundraiser.rsvp_contact && (
                              <div style={{fontSize:"1rem",fontWeight:700,color:"var(--text)",marginBottom:"0.3rem"}}>{selectedFundraiser.rsvp_contact}</div>
                            )}
                            {selectedFundraiser.rsvp_email && (
                              <div><a href={`mailto:${selectedFundraiser.rsvp_email}`} style={{color:"var(--gold)",fontFamily:"var(--font-mono)",fontSize:"0.72rem",textDecoration:"none"}}>{selectedFundraiser.rsvp_email}</a></div>
                            )}
                            {selectedFundraiser.rsvp_phone && (
                              <div style={{fontFamily:"var(--font-mono)",fontSize:"0.72rem",marginTop:"0.15rem"}}><a href={`tel:${selectedFundraiser.rsvp_phone}`} style={{color:"var(--muted)",textDecoration:"none"}}>{selectedFundraiser.rsvp_phone}</a></div>
                            )}
                          </div>
                        </>
                      )}
                      {selectedFundraiser.notes && <div className="fd-notes-label" style={{marginTop: selectedFundraiser.rsvp_contact ? "0.5rem" : 0}}>Notes</div>}
                      {selectedFundraiser.notes && <div>{selectedFundraiser.notes}</div>}
                    </div>
                  )}
                </div>
              </div>
            ) : !selected ? (
              <div className="cal-empty">
                <div className="cal-empty-icon">📅</div>
                <div className="cal-empty-title">Political Calendar</div>
                <div className="cal-empty-sub">Select any committee meeting or fundraiser event from the list to view details.</div>
              </div>
            ) : detailLoading ? (
              <div className="cal-empty">
                <LoadingDots />
                <div style={{fontFamily:"var(--font-mono)",fontSize:"0.7rem",color:"var(--muted)"}}>Loading meeting details...</div>
              </div>
            ) : detail ? (
              <div className="cal-detail">
                {/* Cancellation banner */}
                {detail.cancelled && (
                  <div className="cancelled-banner">
                    <span className="cancelled-icon">🚫</span>
                    <span>This meeting has been <strong>cancelled</strong>. Check the legislature website for rescheduling information.</span>
                  </div>
                )}
                {/* Header */}
                <div className={`detail-header ${detail.chamber}`}>
                  <div className={`detail-chamber-badge ${detail.chamber}`}>
                    {detail.chamber} Committee
                  </div>
                  <div className="detail-title">{detail.committee || detail.committeeName}</div>
                  <div className="detail-meta-grid">
                    <div className="detail-meta-item">
                      <div className="detail-meta-label">Date</div>
                      <div className="detail-meta-value">{formatDisplayDate(detail.date || detail.meetDate)}</div>
                    </div>
                    <div className="detail-meta-item">
                      <div className="detail-meta-label">Time</div>
                      <div className="detail-meta-value">{detail.time || detail.meetTime || "—"}</div>
                    </div>
                    <div className="detail-meta-item">
                      <div className="detail-meta-label">Location</div>
                      <div className="detail-meta-value">{detail.location || "—"}</div>
                    </div>
                    <div className="detail-meta-item">
                      <div className="detail-meta-label">Chair</div>
                      <div className="detail-meta-value">{detail.chair || "—"}</div>
                    </div>
                  </div>
                  <div className="detail-actions">
                    <button className="action-btn primary" onClick={() => downloadIcs(detail.id)}>
                      📥 Add to Calendar (.ics)
                    </button>
                    <button
                      className={`action-btn secondary${trackedMeetingIds.has(detail.id) ? " tracked" : ""}`}
                      onClick={() => handleTrackMeeting(detail)}
                    >
                      {trackedMeetingIds.has(detail.id) ? "📌 Tracked — Click to Remove" : "＋ Track This Meeting"}
                    </button>
                    <button
                      className="action-btn secondary"
                      onClick={() => { setAssignMeeting(detail); setAssignSelected(new Set()); setShowAssign(true); }}
                    >
                      👥 Assign to Client
                    </button>
                    {detail.location && (
                      <a
                        className="action-btn secondary"
                        href={`https://maps.google.com/?q=${encodeURIComponent(detail.location + ", Lansing, MI")}`}
                        target="_blank" rel="noreferrer"
                      >
                        🗺 Directions
                      </a>
                    )}
                    <a className="action-btn secondary" href={detail.link} target="_blank" rel="noreferrer">
                      🔗 View on Legislature Website
                    </a>
                  </div>
                </div>

                {/* Bills */}
                <div className="bills-section">
                  <div className="section-title">
                    Agenda {detail.bills && detail.bills.length > 0 ? `— ${detail.bills.length} Item${detail.bills.length !== 1 ? "s" : ""}` : ""}
                  </div>
                  {detail.bills && detail.bills.length > 0 ? (
                    detail.bills.map((b, i) => (
                      <div className="bill-row" key={i}>
                        {b.type === "bill" ? (
                          <>
                            <div className="bill-number">{b.number}</div>
                            {b.sponsor && <div className="bill-sponsor">Sponsor: {b.sponsor}</div>}
                            <div className="bill-desc">{b.description || "No description available."}</div>
                          </>
                        ) : (
                          <div className="bill-desc" style={{fontStyle:"italic",color:"var(--muted)"}}>{b.description}</div>
                        )}
                      </div>
                    ))
                  ) : (
                    <div className="no-bills">
                      {detailLoading ? <LoadingDots /> : "Agenda not yet published or no bills listed for this meeting."}
                    </div>
                  )}
                </div>
              </div>
            ) : null}
          </div>
        </div>
        {/* ── MOBILE LAYOUT ── */}
        <div className="cal-mobile">
          {mobileView === "list" ? (
            <>
              <div className="cal-mobile-header">
                <div className="cal-mobile-title">Political Calendar</div>
                <div className="cal-mobile-meta">
                  {loading ? "Loading..." : `${filtered.length} meeting${filtered.length !== 1 ? "s" : ""}`}
                  {updated && ` · Updated ${new Date(updated).toLocaleTimeString([], {hour:"2-digit",minute:"2-digit"})}`}
                </div>
              </div>
              {session && (
                <div className="cal-mobile-session">
                  <div className="cal-mobile-session-item">
                    <div className="cal-mobile-session-label house">House</div>
                    <div className="cal-mobile-session-val">{session.house || "Not scheduled"}</div>
                  </div>
                  <div className="cal-mobile-session-item">
                    <div className="cal-mobile-session-label senate">Senate</div>
                    <div className="cal-mobile-session-val">{session.senate || "Not scheduled"}</div>
                  </div>
                </div>
              )}
              <div className="cal-mobile-filters">
                <div className="filter-row">
                  {["All","House","Senate"].map(c => (
                    <button key={c} className={`filter-btn${c === "House" ? " house" : c === "Senate" ? " senate" : ""}${chamberFilter === c ? " active" : ""}`} onClick={() => setChamberFilter(c)}>{c}</button>
                  ))}
                </div>
              </div>
              <div className="cal-mobile-list">
                {loading ? (
                  <MeetingListSkeleton count={6} />
                ) : filtered.length === 0 ? (
                  <div style={{padding:"2rem",textAlign:"center",fontFamily:"var(--font-mono)",fontSize:"0.72rem",color:"var(--muted)"}}>No meetings found</div>
                ) : sortedDates.map(date => (
                  <div key={date}>
                    <div className="cal-mobile-date-header">{formatDisplayDate(date)}</div>
                    {grouped[date].map(m => (
                      <div key={m.id} className="cal-mobile-meeting" onClick={() => selectMeeting(m)}>
                        <div className="cal-mobile-meeting-left">
                          <div className="cal-mobile-meeting-name">{m.committee}</div>
                          <div className="cal-mobile-meeting-meta">
                            {cancelledIds.has(m.id) ? <span style={{color:"#e85555"}}>CANCELLED · </span> : ""}
                            {m.time}{m.chamber ? ` · ${m.chamber}` : ""}
                          </div>
                        </div>
                        <div className="cal-mobile-meeting-arrow">›</div>
                      </div>
                    ))}
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className="cal-mobile-back" onClick={() => { setMobileView("list"); setSelected(null); setDetail(null); }}>
                ‹ Back to Meetings
              </div>
              <div className="cal-mobile-detail">
                {detailLoading ? (
                  <div style={{padding:"2rem",textAlign:"center"}}><LoadingDots /></div>
                ) : detail ? (
                  <div className="cal-detail">
                    {detail.cancelled && (
                      <div className="cancelled-banner">
                        <span className="cancelled-icon">🚫</span>
                        <span>This meeting has been <strong>cancelled</strong>.</span>
                      </div>
                    )}
                    <div className={`detail-header ${detail.chamber}`}>
                      <div className={`detail-chamber-badge ${detail.chamber}`}>{detail.chamber} Committee</div>
                      <div className="detail-title">{detail.committee || detail.committeeName}</div>
                      <div className="detail-meta-grid">
                        <div className="detail-meta-item">
                          <div className="detail-meta-label">Date</div>
                          <div className="detail-meta-value">{formatDisplayDate(detail.date || detail.meetDate)}</div>
                        </div>
                        <div className="detail-meta-item">
                          <div className="detail-meta-label">Time</div>
                          <div className="detail-meta-value">{detail.time || detail.meetTime || "—"}</div>
                        </div>
                        <div className="detail-meta-item">
                          <div className="detail-meta-label">Location</div>
                          <div className="detail-meta-value">{detail.location || "—"}</div>
                        </div>
                        <div className="detail-meta-item">
                          <div className="detail-meta-label">Chair</div>
                          <div className="detail-meta-value">{detail.chair || "—"}</div>
                        </div>
                      </div>
                      <div className="detail-actions">
                        <button className="action-btn primary" onClick={() => downloadIcs(detail.id)}>📥 Add to Calendar</button>
                        <button
                          className={`action-btn secondary${trackedMeetingIds.has(detail.id) ? " tracked" : ""}`}
                          onClick={() => handleTrackMeeting(detail)}
                        >
                          {trackedMeetingIds.has(detail.id) ? "📌 Tracked" : "＋ Track"}
                        </button>
                        <button
                          className="action-btn secondary"
                          onClick={() => { setAssignMeeting(detail); setAssignSelected(new Set()); setShowAssign(true); }}
                        >
                          👥 Assign to Client
                        </button>
                        <a className="action-btn secondary" href={detail.link} target="_blank" rel="noreferrer">🔗 Legislature Website</a>
                      </div>
                    </div>
                    <div className="bills-section">
                      <div className="section-title">Agenda {detail.bills?.length > 0 ? `— ${detail.bills.length} Item${detail.bills.length !== 1 ? "s" : ""}` : ""}</div>
                      {detail.bills?.length > 0 ? detail.bills.map((b, i) => (
                        <div className="bill-row" key={i}>
                          {b.type === "bill" ? (
                            <><div className="bill-number">{b.number}</div>{b.sponsor && <div className="bill-sponsor">Sponsor: {b.sponsor}</div>}<div className="bill-desc">{b.description}</div></>
                          ) : (
                            <div className="bill-desc" style={{fontStyle:"italic",color:"var(--muted)"}}>{b.description}</div>
                          )}
                        </div>
                      )) : (
                        <div className="no-bills">Agenda not yet published.</div>
                      )}
                    </div>
                  </div>
                ) : null}
              </div>
            </>
          )}
        </div>
      </div>
      <ToastContainer toasts={toasts} dismiss={dismiss} />

      {/* ASSIGN TO CLIENT MODAL */}
      {showAssign && assignMeeting && (
        <div className="assign-modal-overlay" onClick={() => setShowAssign(false)}>
          <div className="assign-modal" onClick={e => e.stopPropagation()}>
            <div className="assign-modal-head">
              <div>
                <div className="assign-modal-title">Assign to Client</div>
                <div className="assign-modal-sub">{assignMeeting.committee || assignMeeting.committeeName}{assignMeeting.date ? " · " + assignMeeting.date : ""}</div>
              </div>
              <button className="assign-btn-cancel" onClick={() => setShowAssign(false)}>✕</button>
            </div>
            <div className="assign-modal-body">
              {clients.length === 0 ? (
                <div className="assign-empty">No clients found. Add clients in the Client Services page first.</div>
              ) : clients.map(c => (
                <div
                  key={c.id}
                  className={`assign-client-row${assignSelected.has(c.id) ? " selected" : ""}`}
                  onClick={() => setAssignSelected(prev => {
                    const s = new Set(prev);
                    s.has(c.id) ? s.delete(c.id) : s.add(c.id);
                    return s;
                  })}
                >
                  <div className="assign-client-check">{assignSelected.has(c.id) ? "✓" : ""}</div>
                  <div>
                    <div className="assign-client-name">{c.name}</div>
                    {c.organization && <div className="assign-client-org">{c.organization}</div>}
                  </div>
                </div>
              ))}
            </div>
            <div className="assign-modal-foot">
              <button className="assign-btn-cancel" onClick={() => setShowAssign(false)}>Cancel</button>
              <button
                className="assign-btn-save"
                disabled={assignSelected.size === 0 || assignSaving}
                onClick={handleAssignSave}
              >
                {assignSaving ? "Saving…" : `Log for ${assignSelected.size} Client${assignSelected.size !== 1 ? "s" : ""}`}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
